import { API_BASE } from '@/shared/services/api';
import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/components/ui/select';
import { toast } from 'sonner';
import { Briefcase, Building2, RefreshCw } from 'lucide-react';

const API_URL = API_BASE;
const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('ezerpToken')}`
});

export const PositionsPage = () => {
  const [positions, setPositions] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  const fetchPositions = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/hr/positions`;
      if (departmentFilter !== 'all') url += `?department_id=${departmentFilter}`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to fetch positions');
      const data = await res.json();
      setPositions(Array.isArray(data) ? data : data.positions || []);
    } catch (err) {
      toast.error(err.message);
      setPositions([]);
    } finally {
      setLoading(false);
    }
  }, [departmentFilter]);

  const fetchDepartments = async () => {
    try {
      const res = await fetch(`${API_URL}/api/core/departments`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setDepartments(data.departments || []);
      }
    } catch (err) {
      console.error('Failed to fetch departments:', err);
    }
  };

  useEffect(() => {
    fetchDepartments();
  }, []);

  useEffect(() => {
    fetchPositions();
  }, [fetchPositions]);

  const departmentName = (id) => departments.find((dept) => String(dept.id) === String(id))?.name || 'Unassigned';

  return (
    <div className="space-y-6" data-testid="positions-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">Positions</h1>
          <p className="text-muted-foreground mt-1">Review role and position definitions by department.</p>
        </div>
        <Button variant="outline" onClick={fetchPositions} className="gap-2">
          <RefreshCw className="h-4 w-4" /> Refresh
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div><p className="text-sm text-muted-foreground">Active Positions</p><p className="text-3xl font-bold">{positions.length}</p></div>
            <div className="p-3 rounded-lg bg-purple-500/20"><Briefcase className="h-6 w-6 text-purple-500" /></div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <CardTitle>Position Registry</CardTitle>
            <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
              <SelectTrigger className="w-[220px]"><SelectValue placeholder="Department" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Departments</SelectItem>
                {departments.map((dept) => (
                  <SelectItem key={dept.id} value={String(dept.id)}>{dept.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-green-500" />
            </div>
          ) : positions.length === 0 ? (
            <div className="text-center py-12">
              <Building2 className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <p className="text-lg text-muted-foreground">No positions found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-800/50">
                  <tr>
                    <th className="text-left p-3 font-medium">Title</th>
                    <th className="text-left p-3 font-medium">Department</th>
                    <th className="text-left p-3 font-medium">Code</th>
                    <th className="text-left p-3 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {positions.map((position) => (
                    <tr key={position.id || position.code || position.title} className="hover:bg-slate-800/30">
                      <td className="p-3 font-medium">{position.title || position.name || '-'}</td>
                      <td className="p-3">{departmentName(position.department_id)}</td>
                      <td className="p-3 font-mono text-sm">{position.code || '-'}</td>
                      <td className="p-3"><Badge className="bg-green-500/20 text-green-400">active</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PositionsPage;
